import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, CalendarIcon, ClockIcon, BanknoteIcon, PencilIcon, Trash2Icon, CheckSquareIcon } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
interface ManageTasksPageProps {
  tasks: Task[];
  customers: Customer[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
}
type StatusFilter = 'all' | 'planned' | 'in-progress' | 'pending' | 'todo' | 'done';
type CategoryFilter = 'all' | 'Ladeboks' | 'Andet';
export function ManageTasksPage({
  tasks,
  customers,
  onEditTask,
  onDeleteTask
}: ManageTasksPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch = !query || task.title.toLowerCase().includes(query) || task.taskId?.toLowerCase().includes(query) || task.customerName?.toLowerCase().includes(query);
      // Status filter
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      // Category filter
      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    }).sort((a, b) => {
      // Sort by newest first (using taskId as proxy for creation order)
      const aNum = parseInt(a.taskId?.substring(1) || '0');
      const bNum = parseInt(b.taskId?.substring(1) || '0');
      return bNum - aNum;
    });
  }, [tasks, searchQuery, statusFilter, categoryFilter]);
  const statusConfig: Record<string, {
    label: string;
    color: string;
    bgColor: string;
    dotColor: string;
  }> = {
    planned: {
      label: 'Planlagt',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      dotColor: 'bg-blue-500'
    },
    pending: {
      label: 'Afventende',
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
      dotColor: 'bg-purple-500'
    },
    'in-progress': {
      label: 'I gang',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      dotColor: 'bg-amber-500'
    },
    todo: {
      label: 'Todo',
      color: 'text-stone-700',
      bgColor: 'bg-stone-100',
      dotColor: 'bg-stone-500'
    },
    done: {
      label: 'Afsluttet',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      dotColor: 'bg-emerald-500'
    }
  };
  const statusFilters: {
    value: StatusFilter;
    label: string;
  }[] = [{
    value: 'all',
    label: 'Alle'
  }, {
    value: 'planned',
    label: 'Planlagt'
  }, {
    value: 'in-progress',
    label: 'I gang'
  }, {
    value: 'pending',
    label: 'Afventende'
  }, {
    value: 'todo',
    label: 'Todo'
  }, {
    value: 'done',
    label: 'Afsluttet'
  }];
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-1875">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1876">
        {/* Header Section */}
        <div className="mb-8" data-id="element-1877">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1878">
            Ændre opgaver
          </h1>
          <p className="text-stone-500 mt-2" data-id="element-1879">
            Administrer og rediger alle opgaver i systemet
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-8 shadow-sm" data-id="element-1880">
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-1881">
            {/* Search */}
            <div className="flex-1" data-id="element-1882">
              <div className="relative" data-id="element-1883">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1884" />
                <input type="text" placeholder="Søg efter opgave, kunde eller ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all" data-id="element-1885" />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2" data-id="element-1886">
              <FilterIcon className="w-4 h-4 text-stone-400" data-id="element-1887" />
              <div className="flex p-1 bg-stone-50 rounded-lg border border-stone-200" data-id="element-1888">
                {(['all', 'Ladeboks', 'Andet'] as const).map(cat => <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${categoryFilter === cat ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-1889">
                    {cat === 'all' ? 'Alle' : cat === 'Andet' ? 'Alm. Inst.' : cat}
                  </button>)}
              </div>
            </div>
          </div>

          {/* Status Chips */}
          <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-stone-100" data-id="element-1890">
            <span className="text-sm font-medium text-stone-500 mr-2" data-id="element-1891">
              Status:
            </span>
            {statusFilters.map(filter => <button key={filter.value} onClick={() => setStatusFilter(filter.value)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${statusFilter === filter.value ? 'bg-stone-800 text-white border-stone-800 shadow-sm' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50'}`} data-id="element-1892">
                {filter.label}
              </button>)}
          </div>
        </div>

        {/* Task List */}
        <div className="space-y-3" data-id="element-1893">
          <AnimatePresence mode="popLayout" data-id="element-1894">
            {filteredTasks.length === 0 ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-2xl border border-stone-200 border-dashed p-12 flex flex-col items-center justify-center text-center" data-id="element-1895">
                <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mb-4" data-id="element-1896">
                  <CheckSquareIcon className="w-8 h-8 text-stone-300" data-id="element-1897" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-1" data-id="element-1898">
                  Ingen opgaver fundet
                </h3>
                <p className="text-stone-500 max-w-sm" data-id="element-1899">
                  Vi kunne ikke finde nogen opgaver der matcher dine nuværende
                  filtre og søgekriterier.
                </p>
                {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && <button onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setCategoryFilter('all');
            }} className="mt-6 text-teal-600 font-medium hover:text-teal-700 transition-colors" data-id="element-1900">
                    Nulstil filtre
                  </button>}
              </motion.div> : filteredTasks.map((task, index) => {
            const status = statusConfig[task.status] || statusConfig.todo;
            return <motion.div layout initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.2,
              delay: index * 0.03
            }} key={task.id} className="group bg-white rounded-2xl border border-stone-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all" data-id="element-1901">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-1902">
                      {/* Left Column: Status & Title */}
                      <div className="flex-1 min-w-0" data-id="element-1903">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1904">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bgColor} ${status.color}`} data-id="element-1905">
                            <div className={`w-1.5 h-1.5 rounded-full ${status.dotColor}`} data-id="element-1906" />
                            {status.label}
                          </div>
                          <span className="text-xs font-medium text-stone-400 bg-stone-100 px-2 py-1 rounded-md" data-id="element-1907">
                            {task.taskId}
                          </span>
                          <span className="text-xs font-medium text-stone-500" data-id="element-1908">
                            {task.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 truncate" data-id="element-1909">
                          {task.title}
                        </h3>
                        <p className="text-sm text-stone-500 mt-1 truncate" data-id="element-1910">
                          {task.customerName || 'Ingen kunde'}
                        </p>
                      </div>

                      {/* Middle Column: Dates */}
                      <div className="flex-1 flex flex-col gap-2 min-w-[200px]" data-id="element-1911">
                        {task.agreedPlanning && <div className="flex items-center gap-2 text-sm text-stone-600" data-id="element-1912">
                            <CalendarIcon className="w-4 h-4 text-stone-400" data-id="element-1913" />
                            <span className="font-medium text-stone-700" data-id="element-1914">
                              Proj:
                            </span>
                            {formatDateTime(task.agreedPlanning)}
                          </div>}
                        {task.agreedInstallation && <div className="flex items-center gap-2 text-sm text-stone-600" data-id="element-1915">
                            <CalendarIcon className="w-4 h-4 text-stone-400" data-id="element-1916" />
                            <span className="font-medium text-stone-700" data-id="element-1917">
                              Inst:
                            </span>
                            {formatDateTime(task.agreedInstallation)}
                          </div>}
                        {!task.agreedPlanning && !task.agreedInstallation && <span className="text-sm text-stone-400 italic" data-id="element-1918">
                            Ingen datoer planlagt
                          </span>}
                      </div>

                      {/* Right Column: Price & Hours */}
                      <div className="flex-1 flex flex-col items-start lg:items-end gap-2" data-id="element-1919">
                        <div className="flex items-center gap-2 text-sm" data-id="element-1920">
                          <BanknoteIcon className="w-4 h-4 text-stone-400" data-id="element-1921" />
                          <span className="font-semibold text-stone-900" data-id="element-1922">
                            {(Number(task.totalPrice) || 0).toLocaleString('da-DK')}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm" data-id="element-1923">
                          <ClockIcon className="w-4 h-4 text-stone-400" data-id="element-1924" />
                          <span className="text-stone-600" data-id="element-1925">
                            {task.hours || 0} timer
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-4 lg:pt-0 border-t lg:border-t-0 border-stone-100 lg:pl-4 lg:border-l" data-id="element-1926">
                        <button onClick={() => onEditTask(task)} className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-stone-50 hover:bg-teal-50 text-stone-700 hover:text-teal-700 rounded-xl font-medium transition-colors border border-stone-200 hover:border-teal-200" data-id="element-1927">
                          <PencilIcon className="w-4 h-4" data-id="element-1928" />
                          <span className="lg:hidden" data-id="element-1929">Rediger</span>
                        </button>
                        <button onClick={() => onDeleteTask(task)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-200" title="Slet opgave" data-id="element-1930">
                          <Trash2Icon className="w-4 h-4" data-id="element-1931" />
                        </button>
                      </div>
                    </div>
                  </motion.div>;
          })}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}